/*
 * Typed wrappers over the goserver session allocation endpoints (SPEC.md, PROD-9977).
 */

import {
  get_api as getApi,
  post_api as postApi,
  put_api as putApi
} from '../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../utils/urls';
import type {
  Allocation,
  AllocationBoard,
  FillResult,
  JoinNowItem,
  PromptEvent,
  PromptResult,
  SaveResult
} from './types';

const BASE = '/v2/sales_task_list/session_allocation';

interface Envelope<T> {
  data?: { data?: T };
}

export interface AllocApiError {
  response?: {
    status?: number;
    data?: { reason?: string; message?: string; data?: { allocation?: Allocation | null } };
  };
}

const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export const normalizeBoard = (d: Partial<AllocationBoard> | undefined): AllocationBoard => ({
  now: d?.now || '',
  can_edit: !!d?.can_edit,
  me: d?.me || null,
  sessions: arr<AllocationBoard['sessions'][number]>(d?.sessions).map((s) => ({
    ...s,
    warnings: arr(s.warnings),
    allocation: s.allocation || null,
    cant_join: s.cant_join || null,
    task: s.task || null
  })),
  reps: arr(d?.reps)
});

export const fetchBoard = (): Promise<AllocationBoard> =>
  getApi(getApiUrl(BASE), true).then((res: Envelope<Partial<AllocationBoard>>) =>
    normalizeBoard(res?.data?.data)
  );

// version 0 = no row yet; ops_uuid '' clears.
export const saveAllocation = (
  sessionUuid: string,
  opsUuid: string,
  version: number
): Promise<SaveResult> =>
  putApi(
    getApiUrl(`${BASE}/${encodeURIComponent(sessionUuid)}`),
    { ops_uuid: opsUuid, version },
    true
  ).then((res: Envelope<Partial<SaveResult>>) => ({
    allocation: res?.data?.data?.allocation || null,
    warnings: arr(res?.data?.data?.warnings)
  }));

export const fillEmptySlots = (): Promise<FillResult> =>
  postApi(getApiUrl(`${BASE}/fill`), {}, true).then((res: Envelope<Partial<FillResult>>) => ({
    filled: arr(res?.data?.data?.filled),
    skipped: arr(res?.data?.data?.skipped)
  }));

export const fetchJoinNow = (): Promise<JoinNowItem[]> =>
  getApi(getApiUrl(`${BASE}/join_now`), true).then((res: Envelope<{ items?: JoinNowItem[] }>) =>
    arr<JoinNowItem>(res?.data?.data?.items)
  );

// recorded=false is a duplicate shown the server skipped; claimed only on "opened".
export const sendPrompt = (
  sessionUuid: string,
  event: PromptEvent,
  reason?: 'cant_join'
): Promise<PromptResult> =>
  postApi(
    getApiUrl(`${BASE}/${encodeURIComponent(sessionUuid)}/prompt`),
    reason ? { event, reason } : { event },
    true
  ).then((res: Envelope<Partial<PromptResult>>) => {
    const d = res?.data?.data;
    return {
      recorded: d?.recorded !== false,
      claimed: !!d?.claimed,
      claim_reason: d?.claim_reason,
      task_uuid: d?.task_uuid || undefined,
      released_task: d?.released_task || null
    };
  });

export const errorStatus = (e: unknown): number | undefined =>
  (e as AllocApiError | null)?.response?.status;

// The current row a 409 carries; undefined when the error is not a stale 409.
export const staleAllocation = (e: unknown): Allocation | null | undefined => {
  const res = (e as AllocApiError | null)?.response;
  if (res?.status !== 409) return undefined;
  return res.data?.data?.allocation || null;
};

export const errorReason = (e: unknown, fallback: string): string => {
  const data = (e as AllocApiError | null)?.response?.data;
  return data?.reason || data?.message || fallback;
};
